<template>
  <AsyncStatusWrapper :status="basketStatus">
    <div class="mx-auto px-5 pt-5 sm:px-10 sm:pt-10 xl:px-28">
      <template v-if="basketCount === 0">
        <BasketEmptyState :count="basketCount" class="space-y-8" />
        <RecommendationsConfigSection
          :bloks="emptyStateRecommendations"
          class="mt-10"
        />
      </template>
      <template v-else>
        <div class="mx-auto max-w-screen-4xl lg:flex">
          <div class="w-full lg:pr-10">
            <BasketHeadline v-if="basketCount" :basket-count="basketCount" />
            <BasketCheeringMessage v-if="shouldShowCheeringMessage" />
            <BasketItems
              v-if="availableItems.length"
              :items="availableItems"
              :has-padding-bottom="shouldShowItemsNotReservedMessage"
            />
            <BasketItemsNotReservedMessage
              v-if="shouldShowItemsNotReservedMessage"
            />
            <template v-if="unavailableItems.length">
              <Headline
                tag="h2"
                :is-uppercase="false"
                size="lg"
                class="text-secondary z-10 my-4 w-full py-2.5"
              >
                {{ $t('basket.unavailable_products') }}
              </Headline>
              <BasketItems :items="unavailableItems" are-unavailable-items />
            </template>
          </div>
          <BasketSummary
            :show-sticky-checkout-button="showStickyCheckoutButton"
            :is-checkout-button-disabled="!availableItems.length"
            class="mt-8 sm:mt-0"
          />
        </div>
        <RecommendationsConfigSection
          :bloks="filledStateRecommendations"
          class="mt-10"
        />
      </template>
      <BasketChooseOneSizeDialog
        v-if="showChooseOneSizeDialog && selectedProductGroup"
        :basket-items="selectedProductGroup.items"
        :product="selectedProductGroup.product"
        @close="closeChooseOneSizeDialog"
        @confirm="handleDialogConfirm"
      />
    </div>
    <template #loading>
      <BasketSkeleton />
    </template>
  </AsyncStatusWrapper>
</template>

<script setup lang="ts">
import { watch, onMounted, ref, computed, effectScope } from 'vue'
import { until, useEventListener, useDebounceFn } from '@vueuse/core'
import {
  type BasketItemGroup,
  getProductsWithMultipleSizes,
} from '#layers/base/utils/basket'
import BasketChooseOneSizeDialog from '~/components/basket/ChooseOneSizeDialog.vue'
import useLocalStorage from '#layers/base/composables/useLocalStorage'
import useCms from '~/composables/cms/useCms'
import RecommendationsConfigSection from '#layers/base/components/recommendations/RecommendationsConfigSection.vue'
import {
  RECO_CONFIG_SLUGS,
  RECO_FRAGMENT_IDS,
} from '#layers/base/constants/recommendations'
import type { SbContentFragmentsPage } from '~/cms/storyblok/types/storyblok'

const route = useRoute()
const config = useRuntimeConfig()
const currentShop = useCurrentShop()

const {
  trackBasket,
  trackChooseOneSizeDialogSeen,
  trackChooseOneSizeDialogKeptAllItems,
  trackChooseOneSizeDialogReducedItems,
  trackChooseOneSizeDialogNotShown,
  trackChooseOneSizeDialogDismissed,
} = useTrackingEvents()

const { isDesktop } = useDeviceType()
const scope = effectScope()

const basket = useAgsBasket()

const {
  count: basketCount,
  items: basketItems,
  status: basketStatus,
  availableItems,
  unavailableItems,
} = basket

// ECOM-2696: optional Storyblok-configured recommendation modules beneath the
// basket. A generic `ContentFragmentsPage` story at content-fragments/basketPage
// (mirrors pages/success.vue) holds two ContentFragment bloks — one per basket
// state — selected by `id` (RECO_FRAGMENT_IDS). Fetched client-side (basket
// state is client-loaded) once per locale; a missing or erroring story renders
// nothing (never fatal — R7). Keyed by locale so a locale switch in the same
// session refetches instead of serving the previous language's config (matches
// useShippingCostHint / useProductSizeGuide).
const { data: basketRecoConfig, fetchBySlug: fetchBasketRecoConfig } =
  useCms<SbContentFragmentsPage>(`basketRecoConfig-${currentShop.value.locale}`)

const loadBasketRecoConfig = async () => {
  if (basketRecoConfig.value) {
    return
  }
  try {
    await fetchBasketRecoConfig(RECO_CONFIG_SLUGS.basket)
  } catch {
    // Optional config — swallow so a CMS error never breaks the basket (R7).
  }
}

const emptyStateRecommendations = computed(
  () =>
    basketRecoConfig.value?.content?.fragments?.find(
      (fragment) => fragment.id === RECO_FRAGMENT_IDS.basketEmpty,
    )?.content ?? [],
)
const filledStateRecommendations = computed(
  () =>
    basketRecoConfig.value?.content?.fragments?.find(
      (fragment) => fragment.id === RECO_FRAGMENT_IDS.basketFilled,
    )?.content ?? [],
)

onMounted(() => {
  void loadBasketRecoConfig()
})

const { showChooseOneSizeModal } = useExperiments()

const shouldShowCheeringMessage = computed(() => (basket.count.value ?? 0) > 0)

const shouldShowItemsNotReservedMessage = computed(
  () => (basket.count.value ?? 0) > 0 && isDesktop.value,
)

const productsWithMultipleSizes = computed(() =>
  getProductsWithMultipleSizes(basketItems.value),
)

const showStickyCheckoutButton = ref(false)

// Interface for storing processed dialog items with their variant combinations
interface ChooseOneSizeDialogProcessedItem {
  productId: string
  variantIds: number[]
}

// Choose one size dialog state
const showChooseOneSizeDialog = ref(false)
const selectedProductGroup = ref<BasketItemGroup | null>(null)
const dialogWasConfirmed = ref(false)

// Use localstorage for processed and dismissed items
const { stored: processedItems, set: setProcessedItems } = useLocalStorage<
  ChooseOneSizeDialogProcessedItem[]
>('choose-one-size-processed-items', [])
const { stored: dismissedProductIds, set: setDismissedProductIds } =
  useLocalStorage<string[]>('choose-one-size-dismissed-products', [])

// Helper functions for managing processed items
const hasBeenProcessed = (productGroup: BasketItemGroup): boolean => {
  const variantIds = productGroup.items.map((item) => item.variant.id).sort()
  return processedItems.value.some(
    (processed) =>
      processed.productId === productGroup.productId &&
      processed.variantIds.length === variantIds.length &&
      processed.variantIds.every((id) => variantIds.includes(id)),
  )
}

const hasBeenDismissed = (productGroup: BasketItemGroup): boolean => {
  return dismissedProductIds.value.includes(productGroup.productId)
}

const markAsProcessed = (productGroup: BasketItemGroup): void => {
  const variantIds = productGroup.items.map((item) => item.variant.id).sort()
  const newProcessedItem: ChooseOneSizeDialogProcessedItem = {
    productId: productGroup.productId,
    variantIds,
  }

  // Remove any existing entries for this productId first
  const updatedItems = processedItems.value.filter(
    (item) => item.productId !== productGroup.productId,
  )
  updatedItems.push(newProcessedItem)
  setProcessedItems(updatedItems)
}

const cleanupProcessedItems = (currentGroups: BasketItemGroup[]): void => {
  const currentProductIds = new Set(
    currentGroups.map((group) => group.productId),
  )
  const updatedItems = processedItems.value.filter((item) =>
    currentProductIds.has(item.productId),
  )
  setProcessedItems(updatedItems)

  const updatedDismissed = dismissedProductIds.value.filter((productId) =>
    currentProductIds.has(productId),
  )
  setDismissedProductIds(updatedDismissed)
}

const onScroll = () => {
  const element = document.getElementById('checkout-button')
  const IsBelowViewport = !element || isElementBelowViewport(element)
  showStickyCheckoutButton.value = IsBelowViewport
}

// Choose one size dialog functions
const openChooseOneSizeDialog = (productGroup: BasketItemGroup) => {
  if (hasBeenDismissed(productGroup)) {
    markAsProcessed(productGroup)
    return
  }

  if (!showChooseOneSizeModal.value) {
    const unprocessedGroups = productsWithMultipleSizes.value.filter(
      (group) => !hasBeenProcessed(group) && !hasBeenDismissed(group),
    )

    if (unprocessedGroups.length > 0) {
      trackChooseOneSizeDialogNotShown()
    }

    markAsProcessed(productGroup)
    return
  }

  selectedProductGroup.value = productGroup
  showChooseOneSizeDialog.value = true
  trackChooseOneSizeDialogSeen()
}

const showNextDialogIfAvailable = () => {
  const remainingGroups = productsWithMultipleSizes.value.filter(
    (group) => !hasBeenProcessed(group) && !hasBeenDismissed(group),
  )

  const [nextGroup] = remainingGroups
  if (nextGroup) {
    setTimeout(() => openChooseOneSizeDialog(nextGroup), 100)
  }
}

const closeChooseOneSizeDialog = () => {
  if (!dialogWasConfirmed.value) {
    trackChooseOneSizeDialogDismissed()
    if (
      selectedProductGroup.value &&
      !dismissedProductIds.value.includes(selectedProductGroup.value.productId)
    ) {
      const updatedDismissed = [
        ...dismissedProductIds.value,
        selectedProductGroup.value.productId,
      ]
      setDismissedProductIds(updatedDismissed)
    }
  }

  dialogWasConfirmed.value = false
  markAsProcessed(selectedProductGroup.value!)

  showChooseOneSizeDialog.value = false
  selectedProductGroup.value = null

  showNextDialogIfAvailable()
}

const handleDialogConfirm = async (
  selection: { type: 'single'; variantId: number } | { type: 'keep_all' },
) => {
  dialogWasConfirmed.value = true

  if (selection.type === 'keep_all') {
    trackChooseOneSizeDialogKeptAllItems()
  } else {
    trackChooseOneSizeDialogReducedItems()
  }

  if (selection.type === 'single') {
    const itemsToRemove = selectedProductGroup.value!.items.filter(
      (item) => item.variant.id !== selection.variantId,
    )

    for (const item of itemsToRemove) {
      const basketItem = basketItems.value.find(
        (basketItem) => basketItem.variant.id === item.variant.id,
      )

      if (basketItem) {
        await basket.removeItem(item)
      }
    }
  }
  markAsProcessed(selectedProductGroup.value!)

  showChooseOneSizeDialog.value = false
  selectedProductGroup.value = null

  showNextDialogIfAvailable()
}

onMounted(async () => {
  onScroll()

  // Tracking immediately on mount does not work
  // because the basket is not yet loaded,
  // so we need to watch for the items to be loaded
  await until(() => basket.status.value !== 'pending').toBeTruthy()
  trackBasket({ basketItems: basket.data.value?.items ?? [] })
})

// Watch for products with multiple sizes
watch(
  productsWithMultipleSizes,
  (newGroups, oldGroups) => {
    if (oldGroups && newGroups.length !== oldGroups.length) {
      cleanupProcessedItems(newGroups)
    }

    const unprocessedGroups = newGroups.filter(
      (group) => !hasBeenProcessed(group) && !hasBeenDismissed(group),
    )

    const [nextGroup] = unprocessedGroups
    if (nextGroup && !showChooseOneSizeDialog.value) {
      openChooseOneSizeDialog(nextGroup)
    }
  },
  { immediate: true },
)

useEventListener('scroll', useDebounceFn(onScroll, 5))

scope.run(() => watch(() => basket.data.value?.items, onScroll))

function isElementBelowViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  return (
    rect.bottom > (window.innerHeight || document.documentElement.clientHeight)
  )
}

const metaLinks = useStaticMetaLinks('basket')

const metaTags = metaTagGenerator({
  robots: 'noindex,follow',
  canonical: sanitizeCanonical(`${config.public.baseUrl}${route.path}`),
})

useHead({
  title: `Basket`,
  ...metaTags,
  link: [...(metaTags.link || []), ...metaLinks.value],
})

defineOptions({ name: 'BasketPage' })
definePageMeta({ pageType: 'basket' })
</script>
